import { FinalizeOcrRequest, GetOcrResponse } from '@diamond/shared/types';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteOcrPaymentProof,
  finalizeOcrPaymentProof,
  getOcrPaymentProofItems,
  OCR_PAYMENT_PROOF_QUERY_KEY,
  uploadOcrPaymentProof,
} from '../queries/payment-proof-ocr';
import { NOTIFICATION_QUERY_KEY } from './useNotification';

export type OcrPaymentProofRefetchInterval =
  UseQueryOptions<GetOcrResponse>['refetchInterval'];

interface UseOcrPaymentProofItemsOptions {
  enabled?: boolean;
  refetchInterval?: OcrPaymentProofRefetchInterval;
}

export function useOcrPaymentProofItems(
  orderPaymentId: string,
  token: string,
  options?: UseOcrPaymentProofItemsOptions
) {
  return useQuery({
    queryKey: [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
    queryFn: () => getOcrPaymentProofItems(token, orderPaymentId),
    enabled: !!orderPaymentId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useUploadOcrPaymentProof(token: string) {
  return useMutation({
    mutationFn: ({
      orderPaymentId,
      files,
    }: {
      orderPaymentId: string;
      files: File[];
    }) => uploadOcrPaymentProof(token, orderPaymentId, files),
  });
}

export function useDeleteOcrPaymentProof(
  token: string,
  orderPaymentId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteOcrPaymentProof(token, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
      });
    },
  });
}

export function useFinalizeOcrPaymentProof(
  token: string,
  orderPaymentId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      paymentId,
      body,
    }: {
      paymentId: string;
      body: FinalizeOcrRequest;
    }) => finalizeOcrPaymentProof(token, paymentId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
      });
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_QUERY_KEY],
      });
    },
  });
}
